<template>
	<div class="summary_table_container" :class="{ is_refreshing: isLoading && rows.length }">
		<table class="summary_table">
			<colgroup>
				<col class="col_label">
				<col v-for="column in columns" :key="column.key" :class="column.colClass">
			</colgroup>
			<!-- Todas las filas, encabezado incluido, reparten por igual el alto
				del contenedor (`rowStyle`, en px desde un ResizeObserver): con
				pocas filas el encabezado crece como las demás y su texto queda
				centrado en los dos ejes (Steve, 2026-10-08; el reparto del
				navegador dejaba el encabezado en una línea). -->
			<thead>
				<tr :style="rowStyle">
					<th scope="col" class="sticky_column header_corner">{{ cornerLabel }}</th>
					<th v-for="column in columns" :key="column.key" scope="col"
						class="head_cell nobreak" :class="column.headClass">
						<span v-if="column.key === 'month' && isCurrentMonth"
							v-tooltip.top="'Mes en curso: los datos aún se están cargando.'">
							{{ column.label }}
						</span>
						<template v-else>{{ column.label }}</template>
					</th>
				</tr>
			</thead>
			<tbody v-if="rows.length">
				<!-- Clic por celda (Steve, 2026-10-08): el nombre lleva al tab de la
					fila (`navigate`); en las filas con drawer, una celda de las
					columnas de mes abre el drawer CON EL MES DE ESA COLUMNA
					(`cell-click`) y las de YTD y de variación no reaccionan; en las
					filas sin drawer (márgenes, EBITDA, No Operacional, ratios)
					cualquier celda navega como el nombre, igual que en el Resumen
					(Phil y Steve, 2026-10-08). -->
				<tr v-for="row in visibleRows" :key="row.key"
					:style="rowStyle"
					:class="[row.theme || 'theme_row_white', `depth_${depthOf(row)}`, { is_emphasis: row.emphasis, is_child: row.parentKey, navigable: isNavigable(row) }]">
					<th scope="row" class="sticky_column row_label" @click="onLabelClick(row)">
						<!-- Nombre + chevron en las filas con cuentas (Ventas, Gastos
							Totales, No Operacional): el chevron pliega y despliega sin
							disparar el clic de la fila. -->
						<div class="label_cell">
							<!-- Nombre en una línea con «…» (CSS) y el completo en el
								tooltip cuando no cabe: la primera columna es fija y la
								tabla no cambia de ancho al desplegar cuentas de nombre
								largo (Steve, 2026-10-07). -->
							<span class="row_label_text" v-tooltip.right="labelTooltip(row)">{{ row.label }}</span>
							<button v-if="row.hasChilds"
								type="button"
								class="row_chevron"
								:aria-expanded="isExpanded(row) ? 'true' : 'false'"
								:aria-label="isExpanded(row) ? 'Plegar cuentas' : 'Desplegar cuentas'"
								@click.stop="$emit('toggle-childs', row.key)">
								<ArrowDown class="row_chevron_icon" :class="{ 'is-rotated': isExpanded(row) }" />
							</button>
						</div>
					</th>
					<td v-for="column in columns" :key="column.key"
						class="has-text-right nobreak"
						:class="[column.cellClass, { cell_navigable: isCellClickable(row, column) }]"
						@click="onCellClick(row, column)">
						<template v-if="column.delta">
							<span class="delta" :class="`tone_${deltaTone(row.deltas[column.delta])}`">
								{{ formatDelta(row.deltas[column.delta], { isMoney: row.isMoney }) }}
							</span>
						</template>
						<template v-else-if="row.values[column.value] == null">
							<span class="empty_value" v-tooltip.top="emptyTooltip">{{ EMPTY_VALUE }}</span>
						</template>
						<template v-else-if="row.isMoney">
							<span class="cell_value" v-tooltip.top="parseToMoney(row.values[column.value])">
								{{ parseToCompactMoney(row.values[column.value]) }}
							</span>
						</template>
						<template v-else>{{ formatPercent(row.values[column.value]) }}</template>
					</td>
				</tr>
			</tbody>
			<!-- Esqueleto con la forma final: la pantalla no salta cuando llegan
				los datos. Sólo en la primera carga — al cambiar de mes se conserva
				el contenido anterior atenuado (is_refreshing). -->
			<tbody v-else-if="isLoading" class="skeleton">
				<tr v-for="index in skeletonRows" :key="index">
					<th scope="row" class="sticky_column"><span class="skeleton_bar label_bar"></span></th>
					<td v-for="column in columns" :key="column.key" :class="column.cellClass">
						<span class="skeleton_bar"></span>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
import moment from "moment";
import ArrowDown from "@/components/svg/ArrowDown.vue";
import { parseToCompactMoney, parseToMoney } from "@/utils/currency";
import { formatPercent } from "@/views/financialReports/utils/periodFormatting";
import { EMPTY_VALUE, deltaTone, formatDelta } from "@/utils/dashboardDelta";

const SKELETON_ROWS = 17;
// Alto natural aproximado de una fila (12 px de letra + 18 px de padding +
// borde). Por debajo no se fija alto: las filas ya no entran y el contenedor
// scrollea; también evita el vaivén apilado, donde el contenedor mide lo que
// su contenido.
const MIN_ROW_HEIGHT = 40;

// Tabla resumen del tab Dashboard: el mes focal contra el mes anterior, el
// mismo mes del año anterior y los acumulados del año (YTD) con sus
// variaciones. Recibe las filas ya normalizadas por el store
// (`dashboardReport.normalizeDashboard`) y no toca Vuex: el tema de fila y
// el tab destino vienen del backend; las variaciones se colorean por signo. Montos en formato
// compacto (D1) con el monto completo en el tooltip; % enteros. Las
// columnas de variación se titulan «% MoM / % YoY / % YTD», sin el símbolo Δ
// (Phil, 2026-10-08: la tabla tiene que ser más chica).
//
// Filas plegables (Steve, 2026-10-06 y 07), con la jerarquía del Resumen: un
// total despliega sus líneas y una línea sus cuentas de nivel 3 (hasta dos
// niveles bajo un total). Una fila con `hasChilds` lleva un chevron; una
// fila con `parentKey` sólo se muestra si TODA su cadena de padres está en
// `expandedRows`, que maneja el tab (arranca plegado).
export default {
	name: "DashboardSummaryTable",
	components: { ArrowDown },
	props: {
		// Mes focal "YYYY-MM": arma las etiquetas de las columnas.
		month: { type: String, required: true },
		rows: { type: Array, default: () => [] },
		isLoading: { type: Boolean, default: false },
		// Mes de inicio del reporte "YYYY-MM" (null = sin piso): explica los
		// «—» de las columnas que caen antes.
		floorMonth: { type: String, default: null },
		// Etiqueta de la celda superior izquierda (vacía por pedido de Steve,
		// 2026-10-06: la moneda es siempre CLP y no hace falta decirlo).
		cornerLabel: { type: String, default: "" },
		// false = ninguna fila navega ni abre drawers (tabla sólo lectura).
		navigable: { type: Boolean, default: true },
		// Claves de los totales desplegados: sus cuentas se muestran.
		expandedRows: { type: Array, default: () => [] },
		// Tabs con drawer mensual: sólo sus filas de dinero abren el drawer
		// desde una celda de mes; null = todas las que tengan tab destino.
		drillTabs: { type: Array, default: null },
	},
	data() {
		// Alto del contenedor (clientHeight), medido por el ResizeObserver;
		// 0 = sin medir (jsdom, o antes del primer layout).
		return { EMPTY_VALUE, skeletonRows: SKELETON_ROWS, containerHeight: 0 };
	},
	mounted() {
		this.observeContainer();
	},
	beforeDestroy() {
		if (this._resizeObserver) this._resizeObserver.disconnect();
	},
	computed: {
		focal() {
			return moment(this.month, "YYYY-MM");
		},
		isCurrentMonth() {
			return this.focal.isSame(moment(), "month");
		},
		rowsByKey() {
			return Object.fromEntries(this.rows.map((row) => [row.key, row]));
		},
		// Profundidad de cada fila (0 = sin padre) siguiendo la cadena de padres.
		depthByKey() {
			const depth = {};
			this.rows.forEach((row) => {
				let level = 0;
				let parent = row.parentKey ? this.rowsByKey[row.parentKey] : null;
				while (parent) {
					level += 1;
					parent = parent.parentKey ? this.rowsByKey[parent.parentKey] : null;
				}
				depth[row.key] = level;
			});
			return depth;
		},
		// Las filas sin padre siempre; una hija sólo si cada padre de su
		// cadena está desplegado (plegar el total esconde también las cuentas
		// de sus líneas).
		visibleRows() {
			return this.rows.filter((row) => {
				let parentKey = row.parentKey;
				while (parentKey) {
					if (!this.expandedRows.includes(parentKey)) return false;
					const parent = this.rowsByKey[parentKey];
					parentKey = parent ? parent.parentKey : null;
				}
				return true;
			});
		},
		columns() {
			const label = (m) => {
				const text = m.locale("es").format("MMM YYYY").replace(".", "");
				return text.charAt(0).toUpperCase() + text.slice(1);
			};
			const focal = this.focal;
			const prevMonth = focal.clone().subtract(1, "month");
			const prevYearMonth = focal.clone().subtract(1, "year");
			// `month` ("YYYY-MM") sólo en las columnas que SON un mes: son las
			// que abren el drawer de ese mes al clickear la celda.
			return [
				{ key: "month", value: "month", month: focal.format("YYYY-MM"), label: label(focal.clone()), headClass: "head_focal", cellClass: "cell_focal cell_month", colClass: "col_value" },
				{ key: "prev_month", value: "prevMonth", month: prevMonth.format("YYYY-MM"), label: label(prevMonth), cellClass: "cell_muted", colClass: "col_value" },
				{ key: "mom", delta: "mom", label: "% MoM", headClass: "head_delta", cellClass: "cell_delta", colClass: "col_delta" },
				{ key: "prev_year_month", value: "prevYearMonth", month: prevYearMonth.format("YYYY-MM"), label: label(prevYearMonth), cellClass: "cell_muted", colClass: "col_value" },
				{ key: "yoy", delta: "yoy", label: "% YoY", headClass: "head_delta", cellClass: "cell_delta", colClass: "col_delta" },
				{ key: "ytd", value: "ytd", label: `YTD ${focal.year()}`, headClass: "head_focal", cellClass: "cell_focal", colClass: "col_value" },
				{ key: "prev_ytd", value: "prevYtd", label: `YTD ${focal.year() - 1}`, cellClass: "cell_muted", colClass: "col_value" },
				{ key: "ytd_delta", delta: "ytd", label: "% YTD", headClass: "head_delta", cellClass: "cell_delta", colClass: "col_delta" },
			];
		},
		// Alto parejo para cada fila (encabezado + visibles) cuando a la tabla
		// le sobra espacio. Con `height: 100%` en la tabla el navegador
		// repartía el sobrante sólo entre las filas del cuerpo y Chrome ignora
		// un alto porcentual en `tr`: se fija en px desde el alto medido del
		// contenedor. Si la cuota baja del alto natural (muchas filas
		// desplegadas, o apilado) no se fija nada y el contenedor scrollea.
		rowStyle() {
			if (!this.containerHeight) return null;
			const height = Math.floor(this.containerHeight / (this.visibleRows.length + 1));
			return height >= MIN_ROW_HEIGHT ? { height: `${height}px` } : null;
		},
		emptyTooltip() {
			if (!this.floorMonth) return "Sin datos para este período.";
			const floor = moment(this.floorMonth, "YYYY-MM").locale("es").format("MMMM YYYY");
			return `Sin datos: el reporte comienza en ${floor}.`;
		},
	},
	methods: {
		// Mide el contenedor (la raíz, que es quien scrollea) y lo sigue en
		// cada cambio de tamaño; sin ResizeObserver (jsdom) queda sin medir.
		observeContainer() {
			if (typeof ResizeObserver === "undefined") return;
			this._resizeObserver = new ResizeObserver(() => {
				this.containerHeight = this.$el.clientHeight;
			});
			this._resizeObserver.observe(this.$el);
		},
		parseToMoney,
		parseToCompactMoney,
		formatPercent,
		deltaTone,
		formatDelta,
		isExpanded(row) {
			return this.expandedRows.includes(row.key);
		},
		depthOf(row) {
			return this.depthByKey[row.key] || 0;
		},
		// Nombre completo para el tooltip cuando no cabe en la columna fija.
		// Umbral en caracteres, conservador y según la sangría (220 px menos
		// padding y chevron, a 12 px, dan ~27 caracteres a una fila plana,
		// ~24 a una línea bajo un total, ~21 a una cuenta bajo una línea).
		// Mostrar el tooltip de más es inofensivo; de menos dejaría un nombre
		// cortado sin forma de leerlo.
		labelTooltip(row) {
			const limit = [27, 24, 21][Math.min(this.depthOf(row), 2)];
			return row.label && row.label.length > limit ? row.label : "";
		},
		// El nombre navega al tab de la fila: toda fila con destino, también
		// las de % (márgenes a Rentabilidad) y las cuentas (como su línea).
		isNavigable(row) {
			return this.navigable && Boolean(row.targetTab);
		},
		onLabelClick(row) {
			if (this.isNavigable(row)) this.$emit("navigate", row);
		},
		// Con `drillTabs`, sólo las filas de DINERO de esos tabs tienen drawer:
		// una fila de ratio («Gastos Totales %») apunta al tab de Gastos pero
		// no tiene un drawer que abrir.
		hasDrawer(row) {
			if (!this.isNavigable(row)) return false;
			if (!this.drillTabs) return true;
			return Boolean(row.isMoney) && this.drillTabs.includes(row.targetTab);
		},
		// Una celda abre el drawer de SU mes sólo si la fila tiene drawer, la
		// columna es un mes y hay dato (las «—» de antes del piso no abren).
		opensDrawer(row, column) {
			return Boolean(column.month) && this.hasDrawer(row) && row.values[column.value] != null;
		},
		// Fila sin drawer (márgenes, EBITDA, No Operacional, ratios): sus
		// celdas navegan al tab de la fila, igual que el nombre; no hay un mes
		// que abrir, así que también las de YTD y variación.
		cellNavigates(row, column) {
			return !this.hasDrawer(row) && this.isNavigable(row) && Boolean(column);
		},
		isCellClickable(row, column) {
			return this.opensDrawer(row, column) || this.cellNavigates(row, column);
		},
		onCellClick(row, column) {
			if (this.opensDrawer(row, column)) this.$emit("cell-click", { row, month: column.month });
			else if (this.cellNavigates(row, column)) this.$emit("navigate", row);
		},
	},
};
</script>

<style lang="scss" scoped>
.summary_table_container {
	overflow-x: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	background: var(--report-bg);
	box-shadow: var(--report-shadow-card);
	transition: opacity 0.2s ease;

	&.is_refreshing {
		opacity: 0.55;
		pointer-events: none;
	}
}

// Anchos fijos de las columnas: la tabla mide exactamente su suma (776 px)
// y el layout del tab le da ese espacio, sin scroll horizontal. Phil
// (2026-10-08): tabla más chica (letra 12 px, columnas de valor y de
// variación más angostas) y la primera columna más ancha para leer más
// nombre. Las columnas de variación («% MoM / YoY / YTD») van más angostas
// aún y con menos padding lateral, para ganar ancho (Steve, 2026-10-08):
// «−100%» a 12 px mide ~36 px.
$label-col: 220px;
$value-col: 80px;
$delta-col: 52px;
$delta-col-padding: 6px;

.summary_table {
	// Con `table-layout: fixed` el ancho tiene que ser explícito (con 100 %
	// la tabla se estiraba a toda la pista del layout y aplastaba los
	// gráficos). Las columnas del <colgroup> mandan: la de nombres mide
	// 180 px aunque una cuenta sea más larga (se recorta con «…» y el nombre
	// completo va en el tooltip), así la tabla no cambia de ancho al
	// desplegar.
	width: $label-col + 5 * $value-col + 3 * $delta-col;
	table-layout: fixed;
	border-collapse: separate;
	border-spacing: 0;
	font-size: 12px;
	color: var(--report-text);
	font-variant-numeric: tabular-nums;

	// Anchos fijos y angostos: la tabla define su propio ancho (776 px) y el
	// layout del tab le da exactamente ese espacio, sin scroll horizontal
	// (Steve, 2026-10-07). Los montos van en formato compacto, así 80 px
	// alcanzan; las variaciones son «−12%».
	.col_label { width: $label-col; }
	.col_value { width: $value-col; }
	.col_delta { width: $delta-col; }

	th, td {
		padding: 9px 10px;
		border-bottom: 1px solid var(--report-divider-soft);
		white-space: nowrap;
		// Bulma fuerza `vertical-align: top` en todo `td, th` de una `table`;
		// con las filas repartidas en el alto disponible (`height: 100%`) el
		// nombre y el chevron quedaban arriba y los montos al medio (Phil,
		// 2026-10-08).
		vertical-align: middle;
	}

	thead th {
		background: var(--report-primary);
		color: var(--report-bg);
		font-weight: 600;
		font-size: 11px;
		letter-spacing: 0.01em;
		border-bottom: none;
		position: sticky;
		top: 0;
		z-index: 2;
	}

	// Etiquetas de columna centradas en su celda (los montos siguen a la
	// derecha); el centrado vertical lo da `vertical-align: middle`.
	.head_cell { text-align: center; }

	.header_corner {
		text-align: left;
		color: var(--report-accent-border);
		font-weight: 500;
	}

	// El mes focal es el hilo de la pantalla: su encabezado se destaca en la
	// tabla y la misma banda marca ese mes en los cuatro gráficos.
	.head_focal { color: var(--report-focus-col-text); }
	.head_delta { background: var(--report-delta-head-bg); }

	// Columnas de variación: padding lateral recortado (ver $delta-col).
	.head_delta, .cell_delta {
		padding-left: $delta-col-padding;
		padding-right: $delta-col-padding;
	}

	// Columna fija: el contenido que se desplaza pasa POR DEBAJO. Las celdas
	// toman el fondo de su fila (las filas de margen % conservan su swatch) y
	// la esquina del encabezado el del header — con `inherit` a secas quedaba
	// transparente (el `tr` del thead no tiene fondo) y las columnas se veían
	// a través de ella al hacer scroll.
	.sticky_column {
		position: sticky;
		left: 0;
		z-index: 1;
		text-align: left;
		background-color: inherit;
	}

	// Va DESPUÉS de .sticky_column (misma especificidad): la esquina es la
	// única celda fija en los dos ejes y tiene que quedar sobre los demás
	// encabezados (z-index 2), si no "Jul 2026" se dibuja encima de ella.
	.header_corner { background-color: var(--report-primary); z-index: 3; }

	tbody .sticky_column { box-shadow: inset -1px 0 0 var(--report-divider-soft); }

	tbody tr {
		background: var(--report-bg);

		// Negrita SÓLO en las filas con énfasis (márgenes, EBITDA, Utilidad):
		// nombre, montos, celdas focales y variaciones van juntos (Phil,
		// 2026-10-08: «los montos en negrita sólo para los que tienen labels en
		// negrita»; lo mismo para los %).
		&.is_emphasis .row_label,
		&.is_emphasis .cell_value,
		&.is_emphasis .cell_focal,
		&.is_emphasis .delta { font-weight: 700; }

		// El puntero sólo donde el clic hace algo: el nombre y las celdas de
		// mes con dato; las de YTD y variación no.
		&.navigable .row_label,
		&.navigable .cell_navigable { cursor: pointer; }
		// Hover: la fila entera toma el gris humo del Resumen, sin subrayado
		// (Phil, 2026-10-08; Steve eligió `--report-surface` ese mismo día: el
		// verde del mes focal que iba antes tapaba la celda focal). La columna
		// fija lo hereda (`background-color: inherit`). Sin contorno de foco en
		// la fila: el que quedaba tras clickear una cuenta y cerrar el modal se
		// veía mal (Steve); el chevron conserva el suyo porque es un botón.
		&.navigable:hover { background: var(--report-surface); }
	}

	.row_label {
		font-weight: 500;
		color: var(--report-text);
	}

	// Nombre a la izquierda y chevron pegado a la derecha, centrado.
	.label_cell {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 6px;
		min-width: 0;
	}

	.row_label_text {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.row_chevron {
		flex-shrink: 0;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		padding: 0;
		border: 0;
		background: transparent;
		cursor: pointer;
		color: var(--report-text-muted-strong);

		&:focus-visible {
			outline: 2px solid var(--report-accent-border);
			outline-offset: 2px;
			border-radius: 4px;
		}
	}

	.row_chevron_icon {
		width: 12px;
		height: 12px;
		transition: transform 0.2s ease;

		&.is-rotated { transform: rotate(180deg); }
	}

	// Fila desplegada bajo su padre: sangría por profundidad (una línea bajo
	// un total, una cuenta bajo una línea), sin énfasis. Una línea nivel 2
	// bajo un total se ve como cualquier otra fila nivel 2 (fondo blanco,
	// celdas focales en teal): Phil, 2026-10-08.
	tbody tr.is_child .row_label { font-weight: 400; }

	tbody tr.depth_1 .row_label { padding-left: 26px; }
	tbody tr.depth_2 .row_label { padding-left: 44px; }

	// Cuentas de nivel 3 (`theme_row_account`): un gris suave SÓLO en la
	// celda del mes focal; el resto de columnas en blanco, como su línea
	// nivel 2 (Phil, 2026-10-08: «sólo respetar el color del mes actual»). El
	// YTD focal también queda blanco. El gris es un token propio
	// (`--report-account-month`): `--report-surface` es casi blanco y en el
	// smoke se leyó como blanco (Steve, 2026-10-08).
	.theme_row_account {
		background: var(--report-bg);
		color: var(--report-text-muted-strong);

		.cell_focal { background: transparent; }
		.cell_month { background: var(--report-account-month); }
	}

	.cell_focal { background: var(--report-focus-col); }
	.cell_muted { color: var(--report-text-muted); }
	.cell_delta { background: var(--report-delta-col-bg); }

	.delta {
		&.tone_positive { color: var(--report-positive); }
		&.tone_negative { color: var(--report-negative); }
		&.tone_neutral { color: var(--report-text-muted); }
	}

	.empty_value { color: var(--report-text-muted-soft); }

	// Filas de margen %: mismo swatch que el Resumen (viene en `row.theme`).
	.theme_row_white { background: var(--report-bg); }
	.theme_row_blue { background: var(--report-row-blue); color: var(--report-row-blue-text); }
	.theme_row_azure { background: var(--report-row-azure); color: var(--report-row-azure-text); }
	.theme_row_yellow { background: var(--report-row-yellow); color: var(--report-row-yellow-text); }
	.theme_row_purple { background: var(--report-row-purple); color: var(--report-row-purple-text); }
	.theme_row_orange { background: var(--report-row-orange); color: var(--report-row-orange-text); }

	// En las filas % de margen el color de la fila manda sobre el gris de
	// las columnas de comparación; la celda focal pierde su fondo y toma el
	// color. SÓLO los temas de margen: el comodín `[class*="theme_row_"]` que
	// había acá alcanzaba también a las líneas nivel 2
	// (`theme_row_detail_bills`) y a las cuentas (`theme_row_account`), y les
	// dejaba la celda del mes focal en blanco y el nombre en negrita (Steve,
	// 2026-10-08).
	tr.theme_row_blue, tr.theme_row_azure, tr.theme_row_yellow,
	tr.theme_row_purple, tr.theme_row_orange {
		.row_label, .cell_muted { color: inherit; font-weight: 600; }
		.cell_focal { background: transparent; }
	}

	.skeleton_bar {
		display: inline-block;
		width: 70%;
		height: 12px;
		border-radius: 6px;
		background: var(--report-divider-soft);
		animation: pulse 1.4s ease-in-out infinite;

		&.label_bar { width: 60%; }
	}
}

@keyframes pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.45; }
}

@media (prefers-reduced-motion: reduce) {
	.skeleton_bar { animation: none; }
	.summary_table_container { transition: none; }
}
</style>
